import { driver } from 'driver.js';
import type { DriveStep } from 'driver.js';
import { useCallback } from 'react';

import 'driver.js/dist/driver.css';

import type { OrderCreationStepKey } from '@/util/vexo';

import {
  ENTRY_IMAGE_TOUR_STEPS,
  ENTRY_TOUR_STEPS,
  ORDERFLOW_TOUR_STEPS,
} from '../tour/tourSteps';

import '../tour/orderflowTour.css';

/** True unless `element` is a CSS selector that matches nothing in the DOM. */
const isAnchorPresent = (element: unknown): boolean => {
  if (typeof element !== 'string') return true;
  return document.querySelector(element) != null;
};

/**
 * Drives a driver.js tour over the given steps, first dropping any whose anchor
 * is not currently mounted (markup-only, LTA, substep- or view-specific
 * elements). An empty result is a no-op, so the tour never opens an overlay
 * pointing at nothing.
 */
const driveSteps = (allSteps: DriveStep[], popoverClass = 'orderflow-tour') => {
  const steps = allSteps.filter((step) => isAnchorPresent(step.element));
  if (steps.length === 0) return;

  const tour = driver({
    showProgress: true,
    allowClose: true,
    overlayColor: 'rgba(28, 28, 28, 0.6)',
    popoverClass,
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Got it',
    steps,
  });
  tour.drive();
};

/**
 * Contextual help tours for the OrderFlow V2 flow.
 *
 * - `startTour(stepKey)` runs the tour for the current wizard step (a `null`
 *   key is a no-op).
 * - `startEntryTour()` runs the text-only tour for the pre-wizard entry/landing
 *   screen at `/orders-v2/create` (the "?" help button).
 * - `startFirstEntryTour()` runs the richer, image-annotated variant used for
 *   the automatic first-visit onboarding.
 */
export function useOrderFlowTour() {
  const startTour = useCallback((stepKey: OrderCreationStepKey | null) => {
    if (!stepKey) return;
    driveSteps(ORDERFLOW_TOUR_STEPS[stepKey] ?? []);
  }, []);

  const startEntryTour = useCallback(() => {
    driveSteps(ENTRY_TOUR_STEPS);
  }, []);

  const startFirstEntryTour = useCallback(() => {
    driveSteps(ENTRY_IMAGE_TOUR_STEPS, 'orderflow-tour orderflow-tour-image');
  }, []);

  return { startTour, startEntryTour, startFirstEntryTour };
}

export default useOrderFlowTour;
